import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/outline';
import { FAQPageJsonLd } from 'next-seo';
import Link from 'next/link';

const faqs = [
  {
    questionName: 'What is the cost of solar panel systems for my home?',
    acceptedAnswerText: (
      <>
        EnergyPal secures the best pricing on top-rated solar and home battery
        systems and passes the wholesale savings onto you. For the best deals
        and latest offers in your area, speak with an EnergyPal Advisor who can
        easily design, customize, and share energy packages to fit your home
        needs.Learn more in our{' '}
        <Link
          className="underline"
          href={'/solar-faq/how-much-do-solar-panels-cost'}
        >
          cost of solar
        </Link>{' '}
        article.
      </>
    ),
  },
  {
    questionName: `What's included in an EnergyPal solar energy package?`,
    acceptedAnswerText:
      'Everything, we make it easy for you to go solar with no hassle. Not only is all equipment part of the package, including high-efficiency solar panels, industry-leading solar electricity inverters, electrical components and an energy monitoring device and app, but all the annoying things are taken care of for you including a site assessment, engineering plans, permit submission and utility applications. Of course full installation and warranties are included, and any applicable taxes and financing fees are included.',
  },
  {
    questionName: `How many solar panels does my home need?`,
    acceptedAnswerText: (
      <>
        There are several factors that we consider to determine{' '}
        <Link
          className="underline"
          href="/solar-faq/how-many-solar-panels-do-i-need-for-my-home"
        >
          how many solar panels your home needs
        </Link>{' '}
        in order to offset your existing utility bill. The first is your
        historical and projected energy usage for the home, and the amount of
        energy you wish to offset (usually ~100%). This sets the goal. To
        achieve the goal, our rapid solar design tool takes into account the
        size and orientation of your roof planes, your latitude, potential
        shading from roof obstructions and trees, your local weather patterns,
        and limitations on panel placement from local fire codes to determine
        the potential production from your roof areas. Then we virtually model
        many types of solar panels on your roof to determine how many can fit
        and where to get the maximum production from the optimal balance of
        equipment cost and quality. The final designs are compared (this all
        happens very rapidly in our software) and then our Advisors provide the
        best designs to you, including the number of solar panels.
      </>
    ),
  },
  {
    questionName: `Does EnergyPal offer solar panel microinverters and power optimizers?`,
    acceptedAnswerText:
      'Yes, EnergyPal offers both solar panel microinverters and string inverters with power optimizers, both made by the industry leading manufacturers (Enphase, SolarEdge) and not lesser-known inferior product manufacturers. Each type of inverter works optimally in different situations, and both are great to install with your solar system. The microinverters tend to do slightly better when your roof has some shading throughout the day, since each panel will be able to independently produce its maximum output. The string inverters with power optimizers will, in ideal sunny conditions with less shading issues, have very slightly higher efficiency in converting DC current from the solar panels to AC current that your home uses. In short, it’s complicated to figure out, so our solar design software and your dedicated EnergyPal advisor will very easily be able to determine and customize the right solution(s) for your home.',
  },
  {
    questionName: `Does EnergyPal offer discounts or rebates on home solar or batteries?`,
    acceptedAnswerText:
      'Yes, EnergyPal secures wholesale prices on industry-leading equipment and installation services, and passes the savings onto you. So, all of our packages are essentially discounted from prices you may see from the large national solar companies that have a lot of overhead costs they need to cover, and from local solar companies that simply don’t have the scale or efficiency to be able to offer low prices. In addition, EnergyPal will provide promotional discounts from manufacturers and financiers when available, as well as discounts for first responders and military personnel. Your EnergyPal Advisor can apply these discounts to your quoted packages.',
  },
];

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export default function PackageFrequentlyAskedQuestions() {
  return (
    <>
      <FAQPageJsonLd
        key={'home-solar-installation-faqs'}
        mainEntity={faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.questionName,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.acceptedAnswerText,
          },
        }))}
      />
      <div className="border-b bg-gray-50">
        <div className="px-4 py-12 mx-auto max-w-7xl sm:py-16 sm:px-6 lg:px-8">
          <div className="mx-auto divide-y-2 divide-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
              Home Solar Installation FAQs
            </h2>
            <dl className="mt-6 space-y-6 divide-y divide-gray-200">
              {faqs.map((faq) => (
                <Disclosure as="div" key={faq.questionName} className="pt-6">
                  {({ open }) => (
                    <>
                      <dt className="text-lg">
                        <Disclosure.Button className="flex items-start justify-between w-full text-left text-gray-400">
                          <span className="font-medium text-gray-900">
                            {faq.questionName}
                          </span>
                          <span className="flex items-center ml-6 h-7">
                            <ChevronDownIcon
                              className={classNames(
                                open ? '-rotate-180' : 'rotate-0',
                                'h-6 w-6 transform'
                              )}
                              aria-hidden="true"
                            />
                          </span>
                        </Disclosure.Button>
                      </dt>
                      <Disclosure.Panel as="dd" className="pr-12 mt-2">
                        <div className="text-base text-gray-500">
                          {faq.acceptedAnswerText}
                        </div>
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              ))}
            </dl>
          </div>
        </div>
      </div>
    </>
  );
}
